import { RestClient, RestError } from "./restClient";
import type { DocumentRow, DocumentScope } from "./documentsApi";

/** Conversations stored as knowledge (spec
 *  2026-10-28-user-conversations-as-knowledge-design §7.2). Every route is a
 *  404 while the backend flag is off, so the reads answer null for it and
 *  callers render nothing. */

export type LearningKind = "rule" | "fact" | "procedure" | "qa";
export const LEARNING_KINDS: LearningKind[] = ["rule", "procedure", "fact", "qa"];

/** What a learning applies to: entity name → a value or a list of values. A
 *  key left out means "applies to all". */
export type AppliesTo = Record<string, string | string[]>;

export interface Learning {
  id: string;
  document_id: number;
  workflow: string;
  kind: LearningKind | string;
  text: string;
  applies_to: AppliesTo;
  status: "active" | "edited" | "retired" | string;
  version: number;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** Chat message ids; sent to the conversation's author only. */
  source_messages?: string[];
}

export interface ConversationDocument extends DocumentRow {
  is_author: boolean;
  /** The source chat — the author only; null for anyone else. */
  session_id: string | null;
  capture_status: string;
  tier: DocumentScope;
  learning_count: number;
  /** The detail route only; null when the stored file is unreadable. */
  summary?: string | null;
}

export interface ConversationDetail {
  document: ConversationDocument;
  learnings: Learning[];
}

export type SaveScope = "personal" | "department" | "company";

export interface SaveResult {
  ok: boolean;
  outcome: "kept" | "nothing_useful" | "skipped_short" | "failed" | string;
  document_id: number | null;
  message: string;
  title?: string;
  learnings?: number;
}

/** One place the caller may save a conversation directly, as the server
 *  computes it (GET /scopes, the save tool's writable_scopes). */
export interface SaveScopeOption {
  scope: SaveScope;
  /** Department id (a string, as egpt_documents.dept_id); department only. */
  dept_id?: string;
  label: string;
}

export interface CaptureState {
  session_id: string;
  status: string;
  /** False once the user said "don't save this conversation". */
  enabled: boolean;
  document_id: number | null;
}

export interface LearningPatch {
  text?: string;
  kind?: string;
  applies_to?: AppliesTo;
}

const BASE = "/v1/conversation-knowledge";

export class ConversationKnowledgeApi {
  constructor(private client: RestClient) {}

  /** GET a route, answering null for the feature-off 404 instead of
   *  throwing: "off" is an expected answer on every page that offers a
   *  control, so it must not log the RestClient's console.error each time.
   *  Any other failure throws a RestError with the server's detail. */
  private async getOrOff<T>(path: string): Promise<T | null> {
    const resp = await this.client.rawResponse(path, { method: "GET" });
    if (resp.status === 404) return null;
    if (!resp.ok) {
      let detail = resp.statusText;
      let payload: unknown;
      try {
        payload = await resp.json();
        const d = (payload as { detail?: unknown })?.detail;
        if (typeof d === "string") detail = d;
      } catch {
        // not JSON — keep statusText
      }
      throw new RestError(resp.status, detail, path, payload);
    }
    return (await resp.json()) as T;
  }

  /** The caller's conversation documents; null while the feature is off. */
  async list(): Promise<ConversationDocument[] | null> {
    const res = await this.getOrOff<{ documents: ConversationDocument[] }>(BASE);
    return res === null ? null : (res.documents ?? []);
  }

  /** Where the caller may save; null while the feature is off. */
  async scopes(): Promise<SaveScopeOption[] | null> {
    const res = await this.getOrOff<{ scopes: SaveScopeOption[] }>(`${BASE}/scopes`);
    return res === null ? null : (res.scopes ?? []);
  }

  get(documentId: number): Promise<ConversationDetail> {
    return this.client.get(`${BASE}/${documentId}`);
  }

  editLearning(learningId: string, patch: LearningPatch): Promise<Learning> {
    return this.client.patch(`${BASE}/learnings/${encodeURIComponent(learningId)}`, patch);
  }

  retireLearning(learningId: string): Promise<Learning> {
    return this.client.post(`${BASE}/learnings/${encodeURIComponent(learningId)}/retire`);
  }

  save(sessionId: string, scope: SaveScope, deptId?: string): Promise<SaveResult> {
    return this.client.post(`${BASE}/${encodeURIComponent(sessionId)}/save`, {
      scope,
      ...(scope === "department" ? { dept_id: deptId } : {}),
    });
  }

  /** null while the feature is off, or when the conversation is not the
   *  caller's (the route answers 404 for both). */
  getCapture(sessionId: string): Promise<CaptureState | null> {
    return this.getOrOff(`${BASE}/${encodeURIComponent(sessionId)}/capture`);
  }

  setCapture(sessionId: string, enabled: boolean): Promise<CaptureState> {
    return this.client.put(`${BASE}/${encodeURIComponent(sessionId)}/capture`, { enabled });
  }

  /** Whether the feature is on: the cheapest route. Any failure but the
   *  feature-off 404 throws. */
  async enabled(): Promise<boolean> {
    return (await this.getSettings()) !== null;
  }

  /** null while the feature is off. */
  getSettings(): Promise<{ opt_out: boolean } | null> {
    return this.getOrOff(`${BASE}/settings/me`);
  }

  setSettings(optOut: boolean): Promise<{ opt_out: boolean }> {
    return this.client.put(`${BASE}/settings/me`, { opt_out: optOut });
  }
}
